<template>
    <!-- The header mirrors HomepageBlogSection so the homepage sections stay consistent. -->
    <section
        v-if="DISCUSSIONS_LAUNCHED"
        class="bg-white px-5 py-16 dark:bg-surface-card lg:px-20"
        aria-labelledby="homepage-discussions-heading"
    >
        <div class="container mx-auto">
            <div class="mb-8 flex items-center justify-between">
                <div>
                    <div class="mb-2 flex items-center gap-3">
                        <h2
                            id="homepage-discussions-heading"
                            class="text-3xl font-semibold text-gray-900 dark:text-white"
                        >
                            Diskusie
                        </h2>
                        <span
                            class="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                        >
                            Nové
                        </span>
                    </div>
                    <p class="text-base text-gray-600 dark:text-gray-400">
                        Opýtaj sa komunity na mentoring, prácu, dobrovoľníctvo, vzdelávanie či sebapoznanie
                    </p>
                </div>
                <NuxtLink
                    to="/diskusie"
                    class="hidden items-center gap-2 font-semibold text-primary-600 transition-colors hover:text-primary-700 md:flex dark:text-primary-400 dark:hover:text-primary-300"
                >
                    <span>Všetky diskusie</span>
                    <span>→</span>
                </NuxtLink>
            </div>

            <!-- A list beside the start card instead of a card row: it still looks finished with only a few threads -->
            <div class="grid gap-4 lg:grid-cols-3 lg:gap-6">
                <div v-if="discussions.length" class="flex flex-col gap-4 lg:col-span-2">
                    <DiscussionCard
                        v-for="discussion in discussions"
                        :key="discussion.slug"
                        :discussion="discussion"
                        show-topic
                    />
                </div>
                <NuxtLink
                    to="/diskusie/nova"
                    class="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-300 p-6 text-center text-gray-700 transition-colors hover:border-primary-600 hover:text-primary-700 dark:border-default dark:text-txt-body dark:hover:text-primary-300"
                    :class="{ 'lg:col-span-3': !discussions.length }"
                >
                    <Icon name="heroicons:plus-circle" class="size-8" aria-hidden="true" />
                    <span class="font-semibold">Začať diskusiu</span>
                    <span class="text-sm text-gray-500 dark:text-txt-caption">
                        Polož otázku, s ktorou ti môže komunita pomôcť
                    </span>
                </NuxtLink>
            </div>

            <!-- Mobile CTA -->
            <div class="mt-6 text-center md:hidden">
                <NuxtLink
                    to="/diskusie"
                    class="inline-flex items-center gap-2 font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                    <span>Všetky diskusie</span>
                    <span>→</span>
                </NuxtLink>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { DISCUSSIONS_LAUNCHED } from '#shared/types/Discussion'
import { useDiscussions } from '@/composables/discussions/useDiscussions'

const { listDiscussions } = useDiscussions()

// Newest threads, so fresh questions get answers. A failed fetch leaves just the start card.
// No `default`: the personalized homepage can render client-only during hydration.
const { data } = await useAsyncData('homepage-discussions', () =>
    DISCUSSIONS_LAUNCHED ? listDiscussions({ sort: 'new', limit: 4 }).catch(() => null) : Promise.resolve(null)
)
const discussions = computed(() => data.value?.items || [])
</script>
